import React, { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router';
import { Tabs } from 'antd';

import TabTitle from '@/components/Common/TabTitle';
import Spinner from '@/components/Common/Spin';
import { useStores } from '@/components/hooks';
import i18n from '@/content';
import appConfig, { SSOTypes } from '@/config/env';
import { INTEGRATION_CONFIG_ROUTE } from '@/config/appRoutes';
import { PUBLIC_ASSETS } from '@/consts';
import { captureErrorForSentry } from '@/components/utils';

import IntegrationCard from './IntegrationCard';
import IntegrationSection from './IntegrationSection';
import IntegrationConfigPage from './IntegrationConfigPage';
import { STORAGE_TYPES } from '@/stores/SettingsStore';
import type { IntegrationType, StorageType, ConnectorType } from '@/stores/SettingsStore/interfaces';

import './index.scss';
import '../Common/Tabs/antOverride.scss';

/** Map SSO types to display info */
const SSO_INFO: Record<SSOTypes, { name: string; logo: string }> = {
    [SSOTypes.cognito]: {
        name: 'Cognito',
        logo: `${PUBLIC_ASSETS}/icons/cognito-logo.svg`,
    },
    [SSOTypes.google]: {
        name: 'Google',
        logo: `${PUBLIC_ASSETS}/icons/g-logo.png`,
    },
    [SSOTypes.microsoft]: {
        name: 'Microsoft',
        logo: `${PUBLIC_ASSETS}/icons/ms-logo.png`,
    },
    [SSOTypes.okta]: {
        name: 'Okta',
        logo: `${PUBLIC_ASSETS}/icons/okta-logo.svg`,
    },
};

/** Map storage types to display info */
const STORAGE_INFO: Record<StorageType, { name: string; logo: string }> = {
    sharepoint: {
        name: 'Dedicated SharePoint Site',
        logo: `${PUBLIC_ASSETS}/icons/sharepoint-logo.svg`,
    },
    gcs: {
        name: 'Google Cloud Storage Bucket',
        logo: `${PUBLIC_ASSETS}/icons/google-cloud-logo.svg`,
    },
};

/** Map connector types to display info */
const CONNECTOR_INFO: Record<ConnectorType, { name: string; logo: string }> = {
    slack: {
        name: 'Slack',
        logo: `${PUBLIC_ASSETS}/icons/slack-logo.svg`,
    },
    salesforce: {
        name: 'Salesforce',
        logo: `${PUBLIC_ASSETS}/icons/salesforce-logo.svg`,
    },
    purview: {
        name: 'Microsoft Purview',
        logo: `${PUBLIC_ASSETS}/icons/purview-logo.svg`,
    },
    sharepoint_connector: {
        name: 'SharePoint Connector',
        logo: `${PUBLIC_ASSETS}/icons/sharepoint-logo.svg`,
    },
};

const Settings: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams<{ type?: string }>();
    const { settingsStore } = useStores();
    const {
        isLoading,
        fetchConfiguredIntegrations,
        fetchStorageKeys,
        isIntegrationConfigured,
    } = settingsStore;

    const nameSpace = 'settings';

    const availableConnectors = appConfig.CONNECTORS || [];
    const availableClassification = (availableConnectors.filter(
        (connector) => connector === 'purview',
    ) as ConnectorType[]);
    const visibleConnectors = (availableConnectors.filter(
        (connector) => connector !== 'purview',
    ) as ConnectorType[]);

    const isViewingIntegrationConfig = !!params.type;

    useEffect(() => {
        const loadData = async (): Promise<void> => {
            try {
                await Promise.all([
                    fetchConfiguredIntegrations(),
                    fetchStorageKeys(),
                ]);
            } catch (e) {
                captureErrorForSentry(e, 'Settings.loadData');
            }
        };
        loadData();
    }, [fetchConfiguredIntegrations, fetchStorageKeys]);

    const handleCardClick = useCallback((type: IntegrationType): void => {
        navigate(`${INTEGRATION_CONFIG_ROUTE}/${type}`);
    }, [navigate]);

    if (isViewingIntegrationConfig) {
        return <IntegrationConfigPage />;
    }

    const generateLicenseId = (): string => {
        const stage = import.meta.env.VITE_STAGE;
        let licenseIdSuffix = stage;

        if (stage && stage.startsWith('prod-')) {
            licenseIdSuffix = stage.substring(5);
        }

        return `spx-00000-${licenseIdSuffix}`;
    };

    // Filter SSO types to exclude cognito (internal)
    const displayedSSOTypes = appConfig.SSO.filter((sso) => sso !== SSOTypes.cognito);

    return (
        <Spinner spinning={isLoading} screenCentered fullHeight>
            <div className="settings-wrapper">
                <TabTitle title={i18n.t(`${nameSpace}.title`)} />
                <div className="inner-wrapper">
                    <Tabs
                        defaultActiveKey="integrations"
                        className="antdesign-tabs-override"
                        type="card"
                        items={[
                            {
                                key: 'integrations',
                                label: 'Integrations',
                                children: (
                                    <div className="integrations-container">
                                        <div className="integrations-sections">
                                            {/* Identity Provider Section */}
                                            {displayedSSOTypes.length > 0 && (
                                                <IntegrationSection title="Identity Provider">
                                                    {displayedSSOTypes.map((ssoType) => {
                                                        const info = SSO_INFO[ssoType];
                                                        return (
                                                            <IntegrationCard
                                                                key={ssoType}
                                                                name={info.name}
                                                                logo={info.logo}
                                                                isConfigured
                                                                isReadOnly
                                                            />
                                                        );
                                                    })}
                                                </IntegrationSection>
                                            )}

                                            {/* Storage Systems Section */}
                                            <IntegrationSection title="Storage Systems">
                                                {STORAGE_TYPES.map((storageType) => {
                                                    const info = STORAGE_INFO[storageType];
                                                    const isConfigured = isIntegrationConfigured(storageType);
                                                    return (
                                                        <IntegrationCard
                                                            key={storageType}
                                                            name={info.name}
                                                            logo={info.logo}
                                                            isConfigured={isConfigured}
                                                            onClick={() => handleCardClick(storageType)}
                                                        />
                                                    );
                                                })}
                                            </IntegrationSection>

                                            {/* Classification & Governance Section */}
                                            {availableClassification.length > 0 && (
                                                <IntegrationSection title="Classification & Governance">
                                                    {availableClassification.map((connectorType) => {
                                                        const info = CONNECTOR_INFO[connectorType];
                                                        if (!info) return null;
                                                        const isConfigured = isIntegrationConfigured(connectorType);
                                                        return (
                                                            <IntegrationCard
                                                                key={connectorType}
                                                                name={info.name}
                                                                logo={info.logo}
                                                                isConfigured={isConfigured}
                                                                onClick={() => handleCardClick(connectorType)}
                                                            />
                                                        );
                                                    })}
                                                </IntegrationSection>
                                            )}

                                            {/* Connectors Section */}
                                            {visibleConnectors.length > 0 && (
                                                <IntegrationSection title="Connectors">
                                                    {visibleConnectors.map((connectorType) => {
                                                        const info = CONNECTOR_INFO[connectorType];
                                                        if (!info) return null;
                                                        const isConfigured = isIntegrationConfigured(connectorType);
                                                        return (
                                                            <IntegrationCard
                                                                key={connectorType}
                                                                name={info.name}
                                                                logo={info.logo}
                                                                isConfigured={isConfigured}
                                                                onClick={() => handleCardClick(connectorType)}
                                                            />
                                                        );
                                                    })}
                                                </IntegrationSection>
                                            )}
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                key: 'license',
                                label: 'License',
                                children: (
                                    <div className="license-tab">
                                        <div className="license-info">
                                            <div className="license-status">
                                                <span className="license-label">Customer License Status:</span>
                                                <span className="license-value active">Active</span>
                                            </div>
                                            <div className="license-id">
                                                <span className="license-label">License ID:</span>
                                                <span className="license-value">{generateLicenseId()}</span>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>
        </Spinner>
    );
});

export default Settings;
